/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import StripeSetup from 'subscriptions/helpers/stripe_setup';

@registerElement('confirm-payment-intent')
export default class ConfirmPaymentIntentElement extends ImpulseElement {
  /**
   * The id of the organization that is confirming the payment intent.
   */
  @property() orgId: string;

  /**
   * The client secret from the payment intent. The name suggests that it's a secret, but it isn't actually.
   */
  @property() clientSecret: string;

  /**
   * The status of the payment intent.
   * See: https://stripe.com/docs/payments/payment-intents/verifying-status
   */
  @property() status: string;

  @target() submitButton: HTMLButtonElement;
  @target() cardDetailsElement: HTMLElement;
  @target() errorElement: HTMLElement;

  private stripeSetup: StripeSetup;

  async connected() {
    this.stripeSetup = new StripeSetup(this.orgId);

    try {
      await this.stripeSetup.initialize();
    } catch {
      this.setErrorMessage('An error occurred while loading the subscription form. Please try again.');
    }
  }

  async handleFormSubmit(event: Event) {
    event.preventDefault();
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('loading', '');
    this.errorElement.hidden = true;

    if (this.requiresPaymentMethod && !this.stripeSetup.formValid) {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('loading');
      this.stripeSetup.checkValidity();
      return;
    }

    try {
      this.toggleFormDisability(true);
      const stripeResponse = await this.confirmCardPayment();
      if (stripeResponse.error) {
        this.setErrorMessage(stripeResponse.error.message || '');
        this.cardDetailsElement.hidden = false;
        this.status = 'requires_payment_method';
      } else {
        this.afterConfirmCardPayment();
      }
    } catch (error) {
      this.setErrorMessage('An error occurred while processing your payment. Please try again.');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('loading');
      this.toggleFormDisability(false);
    }
  }

  private confirmCardPayment() {
    return this.stripe!.confirmCardPayment(this.clientSecret, {
      setup_future_usage: 'off_session', // subscription based model should always be "off_session".
      ...(this.requiresPaymentMethod
        ? { save_payment_method: true, payment_method: this.stripeSetup.paymentMethodAttrs }
        : {}),
    });
  }

  private afterConfirmCardPayment() {
    window.location.reload();
  }

  private toggleFormDisability(value: boolean) {
    if (!this.requiresPaymentMethod) return;
    this.stripeSetup.toggleFormDisability(value);
  }

  private setErrorMessage(message: string) {
    this.errorElement.innerText = message;
    this.errorElement.hidden = false;
  }

  private get stripe() {
    return this.stripeSetup.stripe;
  }

  private get requiresPaymentMethod() {
    return this.status === 'requires_payment_method';
  }
}
